import { InspectionTemplateSection } from "@/shared/models/Inspection";
import { dxtToLocalServerTime } from "@dexteel/mesf-core";
import AddIcon from "@mui/icons-material/Add";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import {
	alpha,
	Box,
	Chip,
	IconButton,
	Tooltip,
	Typography,
} from "@mui/material";
import { ColDef, ICellRendererParams } from "ag-grid-enterprise";

type Props = {
	newRevisionFor: (section: InspectionTemplateSection) => void;
	showContextMenu: Function;
};

const CurrentRevisionCell = ({
	data,
}: ICellRendererParams<InspectionTemplateSection>) => {
	if (!data) return null;

	if (data.Rev === null)
		return (
			<Typography variant="caption" color="text.disabled">
				No template
			</Typography>
		);

	return (
		<Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
			<Chip
				size="small"
				label={`Rev ${data.Rev}`}
				sx={{
					height: 22,
					fontSize: 12,
					fontWeight: 600,
					border: "none",
					color: (theme) => theme.palette.primary.main,
					backgroundColor: (theme) => alpha(theme.palette.primary.main, 0.1),
				}}
			/>
			{data.RevisionCount > 1 && (
				<Typography variant="caption" color="text.secondary">
					{`· ${data.RevisionCount} revisions`}
				</Typography>
			)}
		</Box>
	);
};

export const useTableData = ({ newRevisionFor, showContextMenu }: Props) => {
	const columnDefs: ColDef<InspectionTemplateSection>[] = [
		{
			field: "SectionName",
			headerName: "Section",
			cellRenderer: "agGroupCellRenderer",
			cellStyle: {
				display: "flex",
				alignItems: "center",
				justifyContent: "flex-start",
			},
			minWidth: 20,
			flex: 2,
		},
		{
			colId: "CurrentRevision",
			field: "Rev",
			headerName: "Current Revision",
			cellRenderer: CurrentRevisionCell,
			minWidth: 20,
			flex: 2,
		},
		{
			field: "EffectiveFrom",
			headerName: "Effective From",
			minWidth: 20,
			flex: 1,
			valueFormatter: (params) =>
				params.value ? dxtToLocalServerTime(params.value, "MM/dd/yyyy") : "-",
		},
		{
			field: "ColumnCount",
			headerName: "Columns",
			minWidth: 20,
			flex: 1,
		},
		{
			field: "InspectionCount",
			headerName: "Inspections",
			minWidth: 20,
			flex: 1,
		},
		{
			headerName: "",
			colId: "actions",
			cellRenderer: ({
				data,
			}: ICellRendererParams<InspectionTemplateSection>) => (
				<Box
					sx={{ display: "flex", justifyContent: "center", gap: 1 }}
					className="buttons"
				>
					<Tooltip
						title={data?.Rev === null ? "Write first revision" : "New revision"}
					>
						<IconButton
							size="small"
							onClick={() => data && newRevisionFor(data)}
						>
							<AddIcon fontSize="small" color="primary" />
						</IconButton>
					</Tooltip>
					<IconButton
						size="small"
						title="More"
						onClick={(event) =>
							showContextMenu(event, data, "inspectionTemplateContext")
						}
					>
						<MoreVertIcon fontSize="small" />
					</IconButton>
				</Box>
			),
			wrapText: false,
			autoHeight: false,
			wrapHeaderText: false,
			sortable: false,
			flex: 1,
			minWidth: 120,
			filter: false,
			autoHeaderHeight: true,
		},
	];

	return { columnDefs };
};
